import { useState } from 'react';
import useSWR from 'swr';
import { Modal } from 'carbon-components-react';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import { postMethod } from '../../../api/common';
import { useCustomMutation } from '../../../utils/useCustomMutation';
import {
  formatCurrency,
  formatDate,
  getQueryString,
} from '../../../utils/common.util';

/**
 * Confirms and sends a follow-up ("additional students") invoice for one school.
 * The billed count is always recomputed by the billing service; the modal only
 * echoes back the delta the admin saw so a changed count is rejected with 409.
 */
export default function SupplementaryInvoiceModal({
  isOpen,
  row,
  periodStart,
  periodEnd,
  termLabel,
  closeModal,
  onIssued,
}) {
  return (
    <Modal
      id="supplementary-invoice-modal"
      className="big-modal"
      passiveModal={true}
      open={isOpen}
      modalHeading="Send Additional Students Invoice"
      primaryButtonDisabled={false}
    >
      {isOpen && row && (
        // keyed so every opening starts with fresh state
        <ModalBody
          key={`${row.epobs_account_id}-${periodStart}`}
          row={row}
          periodStart={periodStart}
          periodEnd={periodEnd}
          termLabel={termLabel}
          closeModal={closeModal}
          onIssued={onIssued}
        />
      )}
    </Modal>
  );
}

function ModalBody({
  row,
  periodStart,
  periodEnd,
  termLabel,
  closeModal,
  onIssued,
}) {
  const [countChanged, setCountChanged] = useState(false);

  const params = {
    epobsAccountId: row.epobs_account_id,
    periodStart,
    periodEnd,
  };

  const {
    data: preview,
    isLoading,
    isValidating,
    error: previewError,
    mutate: refetchPreview,
  } = useSWR(
    `school/supplementary-invoice-preview?${getQueryString(params)}`,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  const {
    trigger,
    isMutating,
    error: submitError,
    reset,
  } = useCustomMutation(
    'school/create-supplementary-invoice',
    (url, { arg }) => postMethod(url, arg),
  );

  function handleClose() {
    if (isMutating) return;
    reset();
    closeModal();
  }

  function handleConfirm() {
    if (!preview) return;
    setCountChanged(false);

    trigger(
      { ...params, expectedDelta: preview.delta },
      {
        onSuccess: (res) => {
          const invoiceNumber =
            res?.data?.invoice_number ?? res?.data?.invoiceNumber ?? '';
          onIssued(invoiceNumber, preview.school_name);
        },
        onError: (err) => {
          // another admin issued one or enrolment moved: show the new numbers, never retry
          if (err?.status === 409) {
            setCountChanged(true);
            refetchPreview();
          }
        },
      },
    );
  }

  const canConfirm = !!preview?.can_issue && !isValidating && !isMutating;
  const students = preview?.students_added ?? [];

  return (
    <div>
      {isLoading && <p className="text-14 mt-20 text-grey">Calculating...</p>}

      {previewError && (
        <p className="text-14 mt-20 text-danger">{previewError.message}</p>
      )}

      {preview && (
        <div className="mt-20">
          {countChanged && (
            <p className="text-14 mb-20 text-danger font-bold">
              The student count changed since you opened this. The numbers
              below have been recalculated. Review them and send again if they
              are correct.
            </p>
          )}

          {submitError && submitError.status !== 409 && (
            <p className="text-14 mb-20 text-danger">{submitError.message}</p>
          )}

          {!preview.can_issue && preview.blocked_reason && (
            <p className="text-14 mb-20 text-danger">{preview.blocked_reason}</p>
          )}

          <table className="bx--data-table table-white">
            <tbody>
              <tr>
                <td className="font-bold">School</td>
                <td>{preview.school_name}</td>
                <td className="font-bold">Term</td>
                <td>{termLabel}</td>
              </tr>
              <tr>
                <td className="font-bold">Term Invoice</td>
                <td>{preview.general_invoice_number}</td>
                <td className="font-bold">Students At Invoice</td>
                <td>{preview.students_at_invoice}</td>
              </tr>
              <tr>
                <td className="font-bold">Billed In Earlier Follow-ups</td>
                <td>{preview.supplementary_billed}</td>
                <td className="font-bold">Enrolled Now</td>
                <td>{preview.enrolled_now}</td>
              </tr>
              <tr>
                <td className="font-bold">Students To Bill</td>
                <td className="font-bold">{preview.delta}</td>
                <td className="font-bold">Unit Price (GH₵)</td>
                <td>{formatCurrency(Number(preview.unit_price))}</td>
              </tr>
              <tr>
                <td className="font-bold">Amount (GH₵)</td>
                <td className="font-bold" colSpan="3">
                  {preview.delta} × {formatCurrency(Number(preview.unit_price))}{' '}
                  = {formatCurrency(Number(preview.amount))}
                </td>
              </tr>
            </tbody>
          </table>

          <p className="text-14 font-bold mt-30">
            Students added since {formatDate(preview.students_added_since)} (
            {preview.students_added_unavailable ? '—' : students.length})
          </p>

          {preview.students_added_unavailable ? (
            <p className="text-14 mt-10 text-grey">
              The list of newly added students could not be loaded. The amount
              above is still calculated from current enrolment.
            </p>
          ) : students.length === 0 ? (
            <p className="text-14 mt-10 text-grey">
              No students were added after the last invoice.
            </p>
          ) : (
            <div
              className="mt-10"
              style={{ maxHeight: '260px', overflow: 'auto' }}
            >
              <table className="bx--data-table table-white">
                <thead>
                  <tr>
                    <th>Student ID</th>
                    <th>Name</th>
                    <th>Class</th>
                    <th>Date Added</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={`${s.student_id ?? s.full_name}-${s.date_created}`}>
                      <td>{s.student_id ?? '—'}</td>
                      <td>{s.full_name}</td>
                      <td>{s.class_name ?? '—'}</td>
                      <td>{formatDate(s.date_created)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-12 mt-20 text-grey">
            Students to bill is enrolled now minus everything already billed for
            this term. The list is evidence only and can differ from that number
            when students have also left. The school is emailed when the invoice
            is sent.
          </p>
        </div>
      )}

      <div
        className="mt-30 mb-30"
        style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <CustomButton
          classes="outline no-hover secondary font-bold"
          style={{ flex: '1' }}
          onClick={handleClose}
          disabled={isMutating}
        >
          CANCEL
        </CustomButton>
        <CustomButton
          classes="primary font-bold"
          style={{ flex: '1' }}
          onClick={handleConfirm}
          disabled={!canConfirm}
          isLoading={isMutating}
        >
          {preview?.can_issue
            ? `SEND INVOICE FOR GH₵ ${formatCurrency(Number(preview.amount))}`
            : 'SEND INVOICE'}
        </CustomButton>
      </div>
    </div>
  );
}
